import { Injectable, inject, signal } from '@angular/core';
import { Firestore, collection, getDocsFromServer, doc, setDoc, serverTimestamp } from '@angular/fire/firestore';
import { Functions, httpsCallable } from '@angular/fire/functions';
import { AuthService } from './auth.service';

export type Lang = 'en' | 'it';

export interface TranslationEntry {
  en: string;
  it: string;
  createdAt?: unknown;
  autoTranslated?: boolean;
}

@Injectable({ providedIn: 'root' })
export class TranslationService {
  private db = inject(Firestore);
  private functions = inject(Functions);
  private authService = inject(AuthService);

  readonly currentLang = signal<Lang>(
    (localStorage.getItem('lang') as Lang) ?? 'it'
  );

  private translations = signal<Record<string, TranslationEntry>>({});
  readonly loaded = signal(false);
  private pendingAutoTranslate = new Set<string>();

  async loadAll(): Promise<void> {
    try {
      const snap = await getDocsFromServer(collection(this.db, 'Translations'));
      const map: Record<string, TranslationEntry> = {};
      snap.forEach(d => (map[d.id] = d.data() as TranslationEntry));
      this.translations.set(map);
    } catch {
      // Translations non disponibili — l'app mostra i fallback
    } finally {
      this.loaded.set(true);
    }
  }

  setLang(lang: Lang): void {
    this.currentLang.set(lang);
    localStorage.setItem('lang', lang);
  }

  translate(key: string, params?: Record<string, string | number>): string {
    const entry = this.translations()[key];
    if (!entry) {
      this.triggerAutoTranslate(key);
      return key;
    }
    let text = entry[this.currentLang()] ?? entry['en'] ?? key;
    if (params) {
      Object.entries(params).forEach(([k, v]) => {
        text = text.replaceAll(`{{${k}}}`, String(v));
      });
    }
    return text;
  }

  allTranslations(): Record<string, TranslationEntry> {
    return this.translations();
  }

  async saveEntry(key: string, entry: Partial<TranslationEntry>): Promise<void> {
    const ref = doc(this.db, 'Translations', key);
    const autoTranslated = entry.autoTranslated ?? false;
    await setDoc(ref, { ...entry, autoTranslated }, { merge: true });
    this.translations.update(m => ({
      ...m,
      [key]: { ...m[key], ...entry, autoTranslated },
    }));
  }

  async addEntry(key: string, en: string, it: string): Promise<void> {
    const now = new Date();
    const firestoreEntry = { en, it, createdAt: serverTimestamp(), autoTranslated: false };
    await setDoc(doc(this.db, 'Translations', key), firestoreEntry);
    const localEntry: TranslationEntry = { en, it, createdAt: now, autoTranslated: false };
    this.translations.update(m => ({ ...m, [key]: localEntry }));
  }

  private triggerAutoTranslate(key: string): void {
    if (this.pendingAutoTranslate.has(key)) return;
    if (this.authService.currentUser()?.role !== 'admin') return;
    this.pendingAutoTranslate.add(key);
    const fn = httpsCallable(this.functions, 'autoTranslate');
    fn({ key }).catch(() => {});
  }
}
